/**
 * Vienna's KEMAN — the violin — in the shared 120×120 LINE asset box.
 *
 * The Home tab and all four passcode dots draw the instrument. The chat wallpaper
 * draws its F-HOLES, which is the most economical fragment in this whole run: two
 * marks that name the object they were cut out of, from across a room, at any
 * size, and belong to nothing else.
 *
 * WHY AN INSTRUMENT AND NOT THE OPERA HOUSE
 *
 * The theme's icon is an auditorium — tiers of gilded boxes over red velvet — and
 * it is a beautiful picture and an impossible glyph. An interior has no
 * silhouette: seen small it is a rectangle full of texture, which is what every
 * room is. So the room keeps the menu strip, where 1472px is enough to be a room
 * in, and the tab takes the thing the room exists for.
 *
 * WHAT IS LOAD-BEARING
 *
 *  - THE WAIST. The C-bouts pinching in at the middle are the entire silhouette:
 *    a violin without them is a lute, a mandolin or a spoon. The waist here is 38%
 *    of the lower bout's width AND it is held for ten units rather than pinched at
 *    a point — a C-bout is a run of edge curving the wrong way, and a single
 *    pinch reads as a taper however deep it goes.
 *  - THE LOWER BOUT IS WIDER THAN THE UPPER, and lower. Drawn symmetrically the
 *    body reads as a figure eight, which is a shape and not an instrument.
 *  - THE NECK IS LONG — nearly half the body's length. Shortened, this becomes a
 *    ukulele; that proportion is doing more work than any detail on the body.
 *  - THE SCROLL CURLS. It is four points at tab size and it is still the
 *    difference between a violin and a guitar with a pointed head.
 *
 * Every subpath is wound clockwise so a nonzero fill unions them
 * ([[line-shared-glyph-modules]]); the f-holes are the deliberate exception and
 * are wound the other way so an evenodd fill cuts them out.
 */

const f = ( v: number ) => +v.toFixed( 1 );

/** A polygon in clockwise order (y down), whichever order it arrives in. */
function cw( pts: [ number, number ][] ): string {
  let area = 0;
  for ( let i = 0; i < pts.length; i++ ) {
    const a = pts[ i ], b = pts[ ( i + 1 ) % pts.length ];
    area += a[ 0 ] * b[ 1 ] - b[ 0 ] * a[ 1 ];
  }
  const p = area < 0 ? pts.slice().reverse() : pts;
  return 'M' + p.map( ( q ) => `${ f( q[ 0 ] ) },${ f( q[ 1 ] ) }` ).join( ' L' ) + ' Z';
}

/* ---------------------------------------------------------------- geometry -- */

const CX = 60;

/**
 * How far the head is pulled down toward the body, and the angle the instrument
 * is held at.
 *
 * THE WIDTH PROBLEM, and the wrong answer to it. Measured in the shipped tab tile
 * this glyph came out 53px wide by 111 tall where the chats bubble beside it is
 * 82 by 82 — the narrowest thing in the row, in the slot that is supposed to be
 * the theme's own object, and simultaneously too tall to grow.
 *
 * The first fix pushed the bouts out by a third. It failed, and it failed in a way
 * worth writing down: a violin's BODY is about 1.75 parts long to 1 part wide, and
 * widening it toward square turned the two bouts into flat lozenges stacked either
 * side of a pinch. The result read as a chess pawn on a plate. Body proportion is
 * not a free parameter — it is most of what identifies the object.
 *
 * The width comes from the ANGLE instead. Tilted, the same untouched silhouette
 * puts about 99 units across the tile where upright it put 58, and it costs
 * nothing: a violin at an angle is how the instrument is held, how it is
 * photographed and how every icon of one is drawn. Upright is the unusual pose,
 * not the safe one.
 *
 * The head still comes down, which was the half of the first attempt that worked
 * — the long neck was pushing the glyph into the top of the tile.
 */
const HEAD_DROP = 10;
/** Kept so the call sites read the same; the bouts are NOT widened any more. */
const wx = ( x: number ): number => x;
/** Bring a y in the head down toward the body. */
const hy = ( y: number ): number => +( y + HEAD_DROP ).toFixed( 2 );

/**
 * The pose the TAB draws the instrument in: UPRIGHT (owner 2026-08-16, "home
 * tuşundaki keman da dik olsun").
 *
 * It was turned 32 degrees for one pass, to buy width — tilted, the same
 * silhouette puts about 99 units across the tile where upright it puts 58. The
 * owner wants it standing, so the width has to come from scale instead, and the
 * only reason there is room for that scale is HEAD_DROP: shortening the neck took
 * the glyph from 97 units tall to 87, and every unit off the top is a unit the
 * whole instrument can grow by.
 *
 * A straight ENLARGEMENT about the box centre was tried next, since that is the
 * only lever that moves anything here: the tab sizes the glyph by HEIGHT, so once
 * the instrument is tall enough to fill its slot, raising the width percentage
 * changes nothing at all. At 1.18 it measured 80 by 132 against the chats bubble's
 * 90 by 90 and the owner's read was immediate — too big.
 *
 * It came back down twice from there — 1.18, then its natural size at 68 by 111,
 * then this. At 0.9 it measures about 61 by 100 against the chats bubble's 90 by
 * 90: still the tallest thing in the row, and finally the right weight in it.
 *
 * Worth keeping, since this glyph has now been called too small three times and
 * too big twice: "the Home tab should be the biggest thing in the row" is about
 * PRESENCE, not about area. A tall narrow object reaches that presence well before
 * its area matches a square one's, and pushing it to area parity overshoots.
 *
 * The scale lives here rather than in the tab's sizePct because the tab sizes by
 * HEIGHT: once the instrument fills its slot vertically, changing the width
 * percentage does nothing at all, which cost a round of measurements to work out.
 *
 * Kept as an exported constant rather than deleted, so the tab and any future
 * caller stay one decision rather than two.
 */
export const VIOLIN_POSE = 'translate(60,60) scale(0.9) translate(-60,-60)';

/**
 * The BODY: down the right side from the top of the upper bout to the bottom of
 * the lower one, then back up the mirrored left.
 *
 * Authored as a half and mirrored, because a violin is symmetric about its centre
 * line and hand-authoring both sides is how the two stop matching.
 */
export const VIOLIN_BODY = ( () => {
  // [c1x, c1y, c2x, c2y, x, y] for each cubic down the RIGHT side
  const right: [ number, number, number, number, number, number ][] = [
    [ 74, 42, 84, 49, 84, 57 ],     // out into the upper bout
    [ 84, 63, 73, 62, 71, 68 ],     // IN to the waist — the C-bout, and the whole
                                    // silhouette.
    [ 69.6, 72, 69.6, 74, 71, 78 ], // and HOLD it, slightly concave, for ten units.
                                    // This segment is the whole fix: pinching to a
                                    // single point and immediately swelling again
                                    // gives a gentle taper, which the eye reads as
                                    // a pear. A C-bout is a length of edge that
                                    // curves the WRONG WAY, and it has to be long
                                    // enough to see.
    [ 73, 82, 87, 80, 89, 90 ],     // out to the lower bout, wider than the upper
    [ 91, 102, 75, 109, CX, 109 ],  // down to the bottom centre
  ];
  let d = `M${ CX },${ 41 }`;
  for ( const [ ax, ay, bx, by, x, y ] of right ) {
    d += ` C${ f( wx( ax ) ) },${ f( ay ) } ${ f( wx( bx ) ) },${ f( by ) } ${ f( wx( x ) ) },${ f( y ) }`;
  }
  // back up the LEFT, mirroring every control point about CX
  for ( let i = right.length - 1; i >= 0; i-- ) {
    const [ ax, ay, bx, by ] = right[ i ];
    const prev = i === 0 ? [ CX, 41 ] : [ right[ i - 1 ][ 4 ], right[ i - 1 ][ 5 ] ];
    d += ` C${ f( 2 * CX - wx( bx ) ) },${ f( by ) } ${ f( 2 * CX - wx( ax ) ) },${ f( ay ) }`
      + ` ${ f( 2 * CX - wx( prev[ 0 ] ) ) },${ f( prev[ 1 ] ) }`;
  }
  return `${ d } Z`;
} )();

/** The NECK and fingerboard: long, and the length is the proportion that says
 *  violin rather than ukulele. */
export const VIOLIN_NECK = cw( [
  [ CX - 7, 44 ], [ CX - 5, hy( 20 ) ], [ CX + 5, hy( 20 ) ], [ CX + 7, 44 ],
] );

/** The PEGBOX: a little wider than the neck, with two pegs standing out of it. */
export const VIOLIN_PEGBOX = [
  cw( [ [ CX - 6, hy( 21 ) ], [ CX - 6, hy( 12 ) ], [ CX + 6, hy( 12 ) ], [ CX + 6, hy( 21 ) ] ] ),
  // SHORT pegs. At seven units out they crossed the pegbox like an aerial and the
  // instrument's head read as a mandolin's tuner plate.
  // The two pegs are at the SAME height and the same length. On a real instrument
  // they are staggered, and at this size a stagger reads as one peg being broken.
  cw( [ [ CX - 11.5, hy( 17.6 ) ], [ CX - 11.5, hy( 15.2 ) ], [ CX - 5, hy( 15.2 ) ], [ CX - 5, hy( 17.6 ) ] ] ),
  cw( [ [ CX + 5, hy( 15.2 ) ], [ CX + 5, hy( 17.6 ) ], [ CX + 11.5, hy( 17.6 ) ], [ CX + 11.5, hy( 15.2 ) ] ] ),
].join( ' ' );

/**
 * The SCROLL: the head at the very top, CENTRED on the neck's own axis.
 *
 * It used to be a four-point curl running from CX-15 to CX+3 — a real scroll seen
 * from the side, which is genuinely how the feature looks and is genuinely
 * unreadable here. Against a pegbox and neck that are both symmetric about CX, an
 * asymmetric head does not read as a spiral; it reads as a round thing that has
 * slipped off the top of the instrument, which is exactly what the owner saw.
 *
 * So it is symmetric now, with the curl only suggested: a dome that tucks IN at
 * the bottom on each side, which is what a scroll's underside does where it meets
 * the pegbox. At 22px that tuck is the entire spiral, and it costs nothing at 120.
 */
export const VIOLIN_SCROLL = ( () => {
  const top = hy( 2 ), bot = hy( 12 ), R = 7.5;
  return `M${ CX - R },${ f( bot ) }`
    + ` C${ CX - R },${ f( top + 4 ) } ${ CX - 4 },${ f( top ) } ${ CX },${ f( top ) }`
    + ` C${ CX + 4 },${ f( top ) } ${ CX + R },${ f( top + 4 ) } ${ CX + R },${ f( bot ) }`
    + ` C${ CX + 4 },${ f( bot - 2 ) } ${ CX - 4 },${ f( bot - 2 ) } ${ CX - R },${ f( bot ) } Z`;
} )();

export const VIOLIN_BRIDGE = cw( [
  [ CX - 15, 88 ], [ CX - 11, 82 ], [ CX + 11, 82 ], [ CX + 15, 88 ],
] );

/**
 * The VARNISH SHEEN: a soft crescent down the upper bout's left shoulder.
 *
 * A violin is the most polished object in this whole set — that is most of what
 * makes one beautiful — and a flat fill says nothing about it. This is the one
 * mark that turns the passcode's four instruments from silhouettes into things
 * with a surface.
 *
 * It is held WELL inside the outline. A highlight that reaches the edge stops
 * being light on a curved body and becomes a second contour, which is the trap
 * Ocean's jellyfish crest had to avoid for the same reason.
 */
export const VIOLIN_SHEEN =
  `M${ wx( CX - 14 ) },52`
  + ` C${ wx( CX - 19 ) },62 ${ wx( CX - 18 ) },76 ${ wx( CX - 12 ) },88`
  + ` C${ wx( CX - 15 ) },76 ${ wx( CX - 15 ) },64 ${ wx( CX - 9 ) },54`
  + ` C${ wx( CX - 11 ) },52 ${ wx( CX - 13 ) },51 ${ wx( CX - 14 ) },52 Z`;

/**
 * The SHADE: the mirror of the sheen, down the lower bout's right side.
 *
 * Light on one shoulder alone reads as a scratch; light on one side and shade on
 * the other reads as a round body, which is what a violin is. Two marks, and
 * between them they are the whole difference between a silhouette and an object —
 * which is what the owner's note on this passcode was asking for.
 */
export const VIOLIN_SHADE =
  `M${ wx( CX + 15 ) },62`
  + ` C${ wx( CX + 21 ) },74 ${ wx( CX + 20 ) },92 ${ wx( CX + 11 ) },102`
  + ` C${ wx( CX + 17 ) },90 ${ wx( CX + 17 ) },74 ${ wx( CX + 11 ) },64`
  + ` C${ wx( CX + 13 ) },62 ${ wx( CX + 14 ) },61 ${ wx( CX + 15 ) },62 Z`;

/** The four STRINGS, running from the bridge up over the fingerboard. */
export const VIOLIN_STRINGS = ( () => {
  const out: string[] = [];
  for ( let i = 0; i < 4; i++ ) {
    const bx = wx( CX - 5.4 + i * 3.6 );   // spread at the bridge
    const nx = wx( CX - 3 + i * 2 );       // closer together at the nut
    // FINE. At 1.4 units wide in cream the four strings read as a ladder laid over
    // the instrument; at 0.9 they are what they are, which is four hairs catching
    // the light.
    out.push( cw( [ [ bx - 0.45, 84 ], [ nx - 0.4, hy( 20 ) ], [ nx + 0.4, hy( 20 ) ], [ bx + 0.45, 84 ] ] ) );
  }
  return out.join( ' ' );
} )();

/**
 * One F-HOLE: the ƒ-shaped sound hole, as a path.
 *
 * Built from three pieces — an upper eye, a lower eye and the S-curve between
 * them — because that is what the shape is, and because drawing it as one stroked
 * curve loses the eyes the moment LINE scales the asset down. `dir` mirrors it,
 * since the two holes on a violin face away from each other.
 */
function fHole( cx: number, cy: number, k: number, dir: number ): string {
  // The SPINE, sampled along a cubic S and given a real width, rather than
  // authored as a closed outline that doubles back on itself. The first pass did
  // the latter and the two halves of the S very nearly cancelled: what came
  // through was the two eyes and the crossbar with almost nothing between them,
  // which is a dumbbell.
  const P = ( t: number ): [ number, number ] => {
    const u = 1 - t;
    // control points of the S, in glyph units before scaling
    const p0: [ number, number ] = [ 0, -13 ], p1: [ number, number ] = [ -4.6, -6 ];
    const p2: [ number, number ] = [ 4.6, 6 ], p3: [ number, number ] = [ 0, 13 ];
    const x = u * u * u * p0[ 0 ] + 3 * u * u * t * p1[ 0 ] + 3 * u * t * t * p2[ 0 ] + t * t * t * p3[ 0 ];
    const y = u * u * u * p0[ 1 ] + 3 * u * u * t * p1[ 1 ] + 3 * u * t * t * p2[ 1 ] + t * t * t * p3[ 1 ];
    return [ cx + dir * x * k, cy + y * k ];
  };
  const N = 18;
  const left: string[] = [], rightSide: string[] = [];
  for ( let i = 0; i <= N; i++ ) {
    const t = i / N;
    const [ x, y ] = P( t );
    const [ x2, y2 ] = P( Math.min( 1, t + 0.01 ) );
    const len = Math.hypot( x2 - x, y2 - y ) || 1;
    // the spine is FATTEST in the middle, the way a real f-hole is cut
    const w = ( 1.5 + Math.sin( t * Math.PI ) * 1.6 ) * k;
    const nx = -( y2 - y ) / len * w, ny = ( x2 - x ) / len * w;
    left.push( `${ f( x + nx ) },${ f( y + ny ) }` );
    rightSide.push( `${ f( x - nx ) },${ f( y - ny ) }` );
  }
  const spine = 'M' + left.join( ' L' ) + ' L' + rightSide.reverse().join( ' L' ) + ' Z';
  // the two eyes, at the ends of the spine
  const eye = ( t: number, r: number ): string => {
    const [ x, y ] = P( t );
    const R = r * k;
    return `M${ f( x - R ) },${ f( y ) } a${ f( R ) },${ f( R ) } 0 1,0 ${ f( R * 2 ) },0`
      + ` a${ f( R ) },${ f( R ) } 0 1,0 ${ f( -R * 2 ) },0 Z`;
  };
  // the crossbar, SHORT — on a real instrument the nicks are two small notches,
  // not a bar across the hole
  const bar = `M${ f( cx - 4 * k ) },${ f( cy - 1.2 * k ) } L${ f( cx + 4 * k ) },${ f( cy - 1.2 * k ) }`
    + ` L${ f( cx + 4 * k ) },${ f( cy + 1.2 * k ) } L${ f( cx - 4 * k ) },${ f( cy + 1.2 * k ) } Z`;
  return `${ spine } ${ eye( 0, 2.8 ) } ${ eye( 1, 3.2 ) } ${ bar }`;
}

/** The pair of f-holes in their place on the body. */
export const VIOLIN_FHOLES = `${ fHole( wx( CX - 15 ), 78, 0.9, 1 ) } ${ fHole( wx( CX + 15 ), 78, 0.9, -1 ) }`;

/** The whole instrument as ONE path — body, neck, pegbox and scroll. This is what
 *  the idle tab and the unentered passcode dot draw. */
export const VIOLIN = `${ VIOLIN_BODY } ${ VIOLIN_NECK } ${ VIOLIN_PEGBOX }`
  + ` ${ VIOLIN_SCROLL }`;

/** ONE f-hole, centred and at full size in the 120 box — the wallpaper's grain.
 *
 *  It is the most economical fragment in this run: a mark that names the object it
 *  was cut out of, at any size, and belongs to nothing else. */
export const FHOLE = fHole( 60, 60, 2.6, 1 );

/** A NOTE HEAD, the wallpaper's second element — a small tilted ellipse, the way
 *  it is actually drawn on a stave. */
export const NOTE_HEAD = ( () => {
  const rx = 13, ry = 9.5;
  return `M${ 60 - rx },60 a${ rx },${ ry } 0 1,0 ${ rx * 2 },0`
    + ` a${ rx },${ ry } 0 1,0 ${ -rx * 2 },0 Z`;
} )();

/**
 * A QUAVER — head, stem and flag, as ONE path.
 *
 * The wallpaper scattered NOTE_HEAD on its own and it was never a note: an oval
 * with nothing attached is a pebble, and at wallpaper size it read as dirt on the
 * paper. Same failure Spring's buds had, same fix its tulip needed — the head is
 * not the object, the object is the head plus what holds it.
 *
 * The head is TILTED the way an engraved one is (a level ellipse reads as a
 * bead), and the tilt is baked into sampled POINTS rather than applied with a
 * transform, because every consumer here takes a `d` string and a transform would
 * have to be threaded through all of them.
 *
 * The stem rises from the head's right, which is where a stem goes on a note
 * below the middle line, and the flag comes off its top in ONE sweep. Two sweeps
 * make a semiquaver and read as a scribble at this size.
 */
export const NOTE = ( () => {
  const cx = 52, cy = 84, rx = 15, ry = 10.5;
  const t = -20 * Math.PI / 180, ct = Math.cos( t ), st = Math.sin( t );
  const pts: string[] = [];
  const N = 28;
  for ( let i = 0; i < N; i++ ) {
    const a = i / N * Math.PI * 2;
    const x = Math.cos( a ) * rx, y = Math.sin( a ) * ry;
    pts.push( `${ f( cx + x * ct - y * st ) },${ f( cy + x * st + y * ct ) }` );
  }
  const head = 'M' + pts.join( ' L' ) + ' Z';
  const sx = cx + rx * ct - 3;
  const top = 24;
  const stem = `M${ f( sx ) },${ f( cy - 8 ) } L${ f( sx ) },${ top }`
    + ` L${ f( sx + 5 ) },${ top } L${ f( sx + 5 ) },${ f( cy - 8 ) } Z`;
  const flag = `M${ f( sx + 5 ) },${ top } C${ f( sx + 25 ) },${ top + 8 }`
    + ` ${ f( sx + 26 ) },${ top + 22 } ${ f( sx + 16 ) },${ top + 34 }`
    + ` C${ f( sx + 22 ) },${ top + 20 } ${ f( sx + 18 ) },${ top + 11 } ${ f( sx + 5 ) },${ top + 13 } Z`;
  return `${ head } ${ stem } ${ flag }`;
} )();

/* ------------------------------------------------------- passcode colours --
 * Held here rather than in the passcode component: line-lock (the preview the
 * marketplace screenshot shows) and line-page (the exported PNGs) both read
 * them, and every time this family kept two copies of a colour they drifted.
 */

/**
 * The four entered violins, on the lock screen's upper zone — which the sampled
 * skin makes a warm blush (#F8EDE7), not the auditorium dark it was solved for.
 *
 * THIS RAMP HAD TO INVERT, not shift. The old set ran 2.77 / 4.38 / 7.44 / 10.61
 * climbing a dark ground by getting LIGHTER; read against the pale one it runs
 * 5.06 / 3.20 / 1.89 / 1.32 — backwards, with the top two gone. On a light skin a
 * mark lights up by getting DARKER, and every value here is a new one.
 *
 * It is still one material: a violin's varnish from fresh honey through ochre and
 * chestnut to the near-black rosewood of an old instrument. That is the same
 * thinking the dark version used and the only part of it worth keeping.
 *
 * Measured on the blush: 2.72 / 4.24 / 5.84 / 8.82, monotonic.
 *
 * The bottom of the ramp moved UP when the unentered dot did. The owner asked for
 * a louder OFF, and an OFF at 1.80 sitting under a first ON of 2.02 is not a ramp
 * at all — the empty slot and the first filled one would have been the same mark.
 * Raising the floor of a ramp raises everything standing on it.
 */
export const VIOLIN_PC_COLORS = [ '#C9822B', '#A85E2C', '#8A4B2E', '#6A2F34' ];

/**
 * ONE tone for every unentered violin — the frame never changes per digit.
 *
 * 1.80:1 on the blush, raised from 1.31 (owner 2026-08-16: the unentered dots were
 * not showing enough). An empty slot still reads as empty — the four filled ones
 * run from 2.72 to 8.82, so the quietest entered digit outweighs it by half again
 * and the loudest by five times.
 *
 * For the record of what a sampled skin costs: the value this replaced twice over
 * was #4E3232, which measured 1.40 on the old dark zone — correct there — and
 * 10.00 on this one, where it would have made the EMPTY slot the loudest mark on
 * the lock screen.
 */
export const VIOLIN_PC_OFF = '#CBAF9E';

export const VIOLIN_PC_TILTS = [ -8, 6, -4, 10 ];
